import { Component, Prop, h, Element, State, Host, Event, EventEmitter } from '@stencil/core';
import { alert_info_circle } from 'pn-design-assets/pn-assets/icons.js';

@Component({
  tag: 'pn-date-and-time',
  styleUrl: 'pn-date-and-time.scss',
})
export class PnDateAndTime {
  @Element() hostElement: Element;
  pnDatePicker: HTMLElement = null;
  pnTimeFromHourDropdown: HTMLSelectElement = null;
  pnTimeFromMinuteDropdown: HTMLSelectElement = null;
  pnTimeToHourDropdown: HTMLSelectElement = null;
  pnTimeToMinuteDropdown: HTMLSelectElement = null;

  @Prop() languageCode: string = null;
  @Prop() dateAndTimeLabel: string = '';
  @Prop() dateDaysFromToday: number = 0;
  @Prop() validTimeRangeMinutes: number = 0;
  @Prop() errorMessage: string = '';
  @Prop() dateHelperText: string = '';
  @Prop() timeHelperText: string = '';
  @Prop() disableWeekends: boolean = false;
  @Prop() disabledDates: string = null;
  @Prop() datePlaceholder: string = '';
  @Prop() minFromHour: number;
  @Prop() maxToHour: number;

  @State() language: string = null;
  @State() hourArr: string[] = null;
  @State() minuteArr: string[] = null;
  @State() formValue: string = null;
  @State() invalidTimespan: boolean = false;
  @State() selectedDate: string = null;
  @State() selectedFromHour: number;
  @State() selectedFromMin: number;
  @State() selectedToHour: number;
  @State() selectedToMin: number;
  @State() firstValidDateString: string = '';
  @State() lastValidDateString: string = '';

  @Event() dateandtimerange: EventEmitter<string>;

  componentWillLoad() {
    this.setInitialValues();
  }

  componentDidLoad() {
    this.setDefaultHiddenValue();
    this.addEventHandlers();
  }

  setInitialValues() {
    this.setLanguageCode();
    this.setFirstValidDate();

    if (!this.minFromHour) {
      this.minFromHour = 14;
    }

    if (!this.maxToHour) {
      this.maxToHour = 17;
    }

    this.hourArr = this.getNumbersAsStringArr(this.minFromHour, this.maxToHour, 1);
    this.minuteArr = this.getNumbersAsStringArr(0, 50, 10);
  }

  setFirstValidDate() {
    const date = new Date();
    date.setDate(date.getDate() + this.dateDaysFromToday);

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const endYear = year + 2;

    this.firstValidDateString = `${year.toString()}-${month}-${day}`;
    this.lastValidDateString = `${endYear.toString()}-${month}-${day}`;
  }

  setLanguageCode() {
    if (typeof this.languageCode === 'string' && this.languageCode.trim() === '') {
      this.language = document.querySelector('html').lang;
    } else {
      this.language = this.languageCode;
    }
  }

  setDefaultHiddenValue() {
    this.selectedFromHour = parseInt(this.hourArr[0]);
    this.selectedFromMin = parseInt(this.minuteArr[0]);
    this.selectedToHour = parseInt(this.hourArr[this.hourArr.length -1]);
    this.selectedToMin = parseInt(this.minuteArr[this.minuteArr.length -1 ]);

    this.updateHiddenValue();
  }

addEventHandlers() {
  const boundHandleChange = this.handleChange.bind(this);

  this.pnDatePicker.addEventListener('dateselection', boundHandleChange);
  this.pnTimeFromHourDropdown.addEventListener('change', boundHandleChange);
  this.pnTimeFromMinuteDropdown.addEventListener('change', boundHandleChange);
  this.pnTimeToHourDropdown.addEventListener('change', boundHandleChange);
  this.pnTimeToMinuteDropdown.addEventListener('change', boundHandleChange);
}


handleChange(e: CustomEvent) {

  if (e.target === this.pnDatePicker) {
    this.selectedDate = e.detail.start;
  }

  const target = e.target as HTMLSelectElement;

  const value = target.value;

  if (target.name === this.pnTimeFromHourDropdown.name) {
    this.selectedFromHour = parseInt(value);
  } else if (target.name === this.pnTimeFromMinuteDropdown.name) {
    this.selectedFromMin = parseInt(value);
  } else if (target.name === this.pnTimeToHourDropdown.name) {
    this.selectedToHour = parseInt(value);
  } else if (target.name === this.pnTimeToMinuteDropdown.name) {
    this.selectedToMin = parseInt(value);
  }

  this.updateHiddenValue();
}


  updateHiddenValue() {
    const date = new Date();
    const dateFrom = new Date(date.getFullYear(), date.getMonth(), date.getDate(), this.selectedFromHour, this.selectedFromMin, 0, 0);
    const dateTo = new Date(date.getFullYear(), date.getMonth(), date.getDate(), this.selectedToHour, this.selectedToMin, 0, 0);
    this.validateTimeRange(dateFrom.getTime(), dateTo.getTime());

    if (this.invalidTimespan || !this.selectedDate) {
      this.formValue = '';
    } else {
      const fromMin = this.selectedFromMin.toString().padStart(2, '0');
      const toMin = this.selectedToMin.toString().padStart(2, '0');

      this.formValue = `${this.selectedDate}, ${this.selectedFromHour}:${fromMin}-${this.selectedToHour}:${toMin}`;
    }
    this.dateandtimerange.emit(this.formValue);
  }

  validateTimeRange(from: number, to: number) {
    let differenceValue = (to - from) / 1000;
    differenceValue /= 60;
    this.invalidTimespan = differenceValue < this.validTimeRangeMinutes ? true : false;
  }

  getNumbersAsStringArr(start: number, stop: number, step: number) {
    const arr = Array.from({ length: (stop - start) / step + 1 }, (_, index: number) => start + index * step);
    const result: string[] = [];

    arr.forEach(item => {
      const itemAsString = item.toString().padStart(2, '0');
      result.push(itemAsString);
    });

    return result;
  }

  removePnOptionSelected() {
    const selectOptions = this.hostElement.querySelectorAll('option');
    selectOptions.forEach(elm => {
      elm.removeAttribute('selected');
    });
  }

  render() {
    return (
      <Host>
        <div class="date-and-time-group">
          {this.dateAndTimeLabel ? (
            <div class="date-and-time-row">
              <label>{this.dateAndTimeLabel}</label>
            </div>
          ) : null}
          <div class="date-and-time-row">
            <pn-date-picker
              ref={el => (this.pnDatePicker = el as HTMLInputElement)}
              range="false"
              disable-weekends={this.disableWeekends}
              min-date={this.firstValidDateString}
              max-date={this.lastValidDateString}
              language={this.language}
              placeholder={this.datePlaceholder}
              disabled-dates={this.disabledDates}
            ></pn-date-picker>
          </div>

          {this.dateHelperText ? <small class="date-and-time-row helper-text">{this.dateHelperText}</small> : null}
        </div>
        <div class="date-and-time-group">
          <div class="date-and-time-row">
            <pn-select
              ref={el => (this.pnTimeFromHourDropdown = el as HTMLSelectElement)}
              name="date-and-time-from-hour"
              class="date-and-time-dropdown--time"
              invalid={this.invalidTimespan} >
              {this.hourArr?.map((hour: string, index: number) => <option label={hour} value={hour} selected={index === 0}>{hour}</option>)}
            </pn-select>
            <pn-select
              ref={el => (this.pnTimeFromMinuteDropdown = el as HTMLSelectElement)}
              name="date-and-time-from-minute"
              class="date-and-time-dropdown--time"
              invalid={this.invalidTimespan} >
              {this.minuteArr?.map((minute: string, index: number) => <option label={minute} value={minute} selected={index === 0}>{minute}</option>)}
            </pn-select>
            <div class="time-separator">
              <span>-</span>
            </div>
            <pn-select
              ref={el => (this.pnTimeToHourDropdown = el as HTMLSelectElement)}
              name="date-and-time-to-hour"
              class="date-and-time-dropdown--time"
              invalid={this.invalidTimespan} >
              {this.hourArr?.map((hour: string, index: number) => <option label={hour} value={hour} selected={index === this.hourArr.length -1}>{hour}</option>)}
            </pn-select>
            <pn-select
              ref={el => (this.pnTimeToMinuteDropdown = el as HTMLSelectElement)}
              name="date-and-time-to-minute"
              class="date-and-time-dropdown--time"
              invalid={this.invalidTimespan} >
              {this.minuteArr?.map((minute: string, index: number) => <option label={minute} value={minute} selected={index === this.minuteArr.length -1}>{minute}</option>)}
            </pn-select>
          </div>
          {this.timeHelperText ? <small class="date-and-time-row helper-text">{this.timeHelperText}</small> : null}

          {this.invalidTimespan && this.errorMessage ? (
            <small class="date-and-time-row error-message">
              <pn-icon icon={alert_info_circle} small="false" color="warning" />
              {this.errorMessage}
            </small>
          ) : null}
        </div>
      </Host>
    );
  }
}
